import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import {
    TaskProgressCellVariant,
    TaskProgressCellVariantSchema,
} from "@common/types/views/clientsWorkOverviewView";
import { resolveColumnVariant } from "@design-system/ViewTable/core/resolveColumnVariant";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    ClientsWorkOverviewRow,
    ClientsWorkOverviewWorkRow,
} from "../clientsWorkOverviewViewConfig";
import { CircularTaskProgress } from "./CircularTaskProgress";
import { EMPTY_CELL_VALUE } from "./emptyCellValue";

type WorkTaskProgressRow = Pick<
    ClientsWorkOverviewWorkRow,
    "terminatedTasksCount" | "totalTaskCount"
>;

/** The share of an application's tasks that are finished, 0 to 100. */
export const getWorkTaskProgressPercentage = ({
    terminatedTasksCount,
    totalTaskCount,
}: WorkTaskProgressRow) =>
    Math.round((terminatedTasksCount / totalTaskCount) * 100);

const PROGRESS_TEXT_BY_VARIANT: Record<
    TaskProgressCellVariant,
    (row: WorkTaskProgressRow) => string
> = {
    count: (row) => `${row.terminatedTasksCount} / ${row.totalTaskCount}`,
    percentage: (row) => `${getWorkTaskProgressPercentage(row)}%`,
    circle: (row) =>
        `${row.terminatedTasksCount} / ${row.totalTaskCount} (${getWorkTaskProgressPercentage(row)}%)`,
    remaining: (row) =>
        String(Math.max(0, row.totalTaskCount - row.terminatedTasksCount)),
};

/** The progress as text in the column's variant; the circle reads as the count and percentage. */
export const formatWorkTaskProgress = ({
    row,
    variant,
}: {
    row: WorkTaskProgressRow;
    variant: TaskProgressCellVariant;
}) => PROGRESS_TEXT_BY_VARIANT[variant](row);

/** An application's finished tasks in the column's variant, for work with tasks. */
export const WorkTaskProgress = ({
    row,
    variant,
}: {
    row: WorkTaskProgressRow;
    variant: TaskProgressCellVariant;
}) => {
    if (variant === "circle") {
        const pct = getWorkTaskProgressPercentage(row);
        return (
            <Tooltip
                content={
                    <>
                        {row.terminatedTasksCount} / {row.totalTaskCount}{" "}
                        <span className="text-sm text-gray-500">({pct}%)</span>
                    </>
                }
            >
                <span>
                    <CircularTaskProgress percentage={pct} />
                </span>
            </Tooltip>
        );
    }

    return <span>{formatWorkTaskProgress({ row, variant })}</span>;
};

export const WorkTaskProgressCell = memo(
    ({
        row,
        column,
    }: CellContext<ViewTableFeatures, ClientsWorkOverviewRow, number>) => {
        if (row.original.rowType !== "work") {
            return EMPTY_CELL_VALUE;
        }

        if (row.original.totalTaskCount === 0) {
            return EMPTY_CELL_VALUE;
        }

        const variant = resolveColumnVariant({
            meta: column.columnDef.meta,
            schema: TaskProgressCellVariantSchema,
            fallback: "count",
        });

        return <WorkTaskProgress row={row.original} variant={variant} />;
    },
);

WorkTaskProgressCell.displayName = "WorkTaskProgressCell";
